// Reusable searchable object picker.
//
// Replaces the standard <select> for "pick an object name" fields. Renders
// a button showing the current selection; clicking opens an anchored panel
// containing a search box and a scrollable filtered list. Long catalogs
// (hundreds/thousands of objects) stay usable because:
//   - the panel is sized independently of the field
//   - text search filters by obj_name, title, and any extra hint string
//   - keyboard: ↑/↓ moves selection, Enter picks, Escape closes
// The panel also exposes a "Use custom name" mode for objects that the
// user plans to create later — they type a name and it is accepted as-is.
//
// All styling is inline so the component drops into the existing property
// panel without needing a CSS update.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, X, Edit2 } from 'lucide-react'

export interface ObjectPickerEntry {
  obj_name: string
  title?: string
  hint?: string                  // optional secondary text (visual type, schema, etc.)
}

interface Props {
  label?: string
  value: string
  onChange: (next: string) => void
  catalog: ObjectPickerEntry[]
  typeLabel?: string             // e.g. "transaction" — used in placeholders
  placeholder?: string
  allowCustom?: boolean          // default true; when false, no custom-name input
  emptyHint?: string             // shown when catalog is empty
  disabled?: boolean
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const TRIGGER: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 8,
  textAlign: 'left',
}

const SEARCH_INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px 6px 30px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

const PANEL_HEIGHT = 320       // px, the popover's max height
const ITEM_HEIGHT = 44         // approx; not virtualized but capped by overflow

export default function ObjectPickerField({
  label,
  value,
  onChange,
  catalog,
  typeLabel = 'object',
  placeholder,
  allowCustom = true,
  emptyHint,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Resolve the current selection's display label (so the trigger shows
  // a friendly name when available, not just the raw obj_name).
  const current = useMemo(
    () => catalog.find((c) => c.obj_name === value),
    [catalog, value],
  )
  const isCustom = !!value && !current

  return (
    <div>
      {label && <label style={FIELD_LABEL}>{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          ...TRIGGER,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {value ? (
            <>
              <span style={{
                fontSize: '0.9125rem', color: 'var(--color-text)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {current?.title && current.title !== current.obj_name
                  ? current.title
                  : value}
              </span>
              <span style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {value}{isCustom ? ' · custom' : ''}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9125rem' }}>
              {placeholder || `Pick a ${typeLabel}…`}
            </span>
          )}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
      </button>

      {open && (
        <PickerPopover
          anchor={triggerRef.current}
          catalog={catalog}
          value={value}
          typeLabel={typeLabel}
          allowCustom={allowCustom}
          emptyHint={emptyHint}
          onClose={() => setOpen(false)}
          onPick={(v) => { onChange(v); setOpen(false) }}
        />
      )}
    </div>
  )
}

// ─── Popover (anchored to the trigger) ────────────────────────

function PickerPopover({
  anchor,
  catalog,
  value,
  typeLabel,
  allowCustom,
  emptyHint,
  onClose,
  onPick,
}: {
  anchor: HTMLElement | null
  catalog: ObjectPickerEntry[]
  value: string
  typeLabel: string
  allowCustom: boolean
  emptyHint?: string
  onClose: () => void
  onPick: (next: string) => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [customMode, setCustomMode] = useState(false)
  const [customName, setCustomName] = useState(
    !catalog.some((c) => c.obj_name === value) ? value : '',
  )

  // Position the panel beneath/over the trigger. Falls back to centred if
  // the anchor is off-screen.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  useEffect(() => {
    if (!anchor) {
      setPos({ top: 80, left: window.innerWidth / 2 - 200, width: 400 })
      return
    }
    const update = () => {
      const rect = anchor.getBoundingClientRect()
      const width = Math.max(rect.width, 280)
      const spaceBelow = window.innerHeight - rect.bottom
      const top = spaceBelow < PANEL_HEIGHT + 24 && rect.top > PANEL_HEIGHT + 24
        ? rect.top - PANEL_HEIGHT - 4
        : rect.bottom + 4
      const left = Math.min(rect.left, window.innerWidth - width - 8)
      setPos({ top, left, width })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])

  // Auto-focus the search box on mount
  useEffect(() => {
    requestAnimationFrame(() => searchRef.current?.focus())
  }, [])

  // Escape / outside-click handling
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target)) return
      if (anchor?.contains(target)) return
      onClose()
    }
    window.addEventListener('keydown', onKey)
    // mousedown — fires before focus changes, avoids race with the trigger
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [anchor, onClose])

  // Filtered list — query matches obj_name, title, hint (case-insensitive).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter((c) => {
      return (
        c.obj_name.toLowerCase().includes(q)
        || (c.title || '').toLowerCase().includes(q)
        || (c.hint || '').toLowerCase().includes(q)
      )
    })
  }, [query, catalog])

  // Reset / clamp highlight when filter changes
  useEffect(() => {
    setHighlight(0)
  }, [query])

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(filtered.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = filtered[highlight]
      if (pick) onPick(pick.obj_name)
    }
  }

  if (!pos) return null

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: pos.top, left: pos.left, width: pos.width,
        maxHeight: PANEL_HEIGHT,
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: 2000,
        overflow: 'hidden',
      }}
    >
      {/* Header — search + close */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: 8, borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search
            size={13}
            style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--color-text-secondary)', pointerEvents: 'none',
            }}
          />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onListKeyDown}
            placeholder={`Search ${typeLabel}s by name, title, or type…`}
            style={SEARCH_INPUT}
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            background: 'transparent', border: 'none', padding: 4, cursor: 'pointer',
            color: 'var(--color-text-secondary)', borderRadius: 4,
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* List */}
      {!customMode && (
        <div
          role="listbox"
          tabIndex={-1}
          style={{
            flex: 1, overflowY: 'auto',
            display: 'flex', flexDirection: 'column',
          }}
          onKeyDown={onListKeyDown}
        >
          {filtered.length === 0 ? (
            <div style={{
              padding: '20px 14px',
              textAlign: 'center',
              fontSize: '0.8625rem',
              color: 'var(--color-text-secondary)',
            }}>
              {catalog.length === 0
                ? (emptyHint || `No ${typeLabel}s in this project yet.`)
                : `No ${typeLabel}s match "${query}".`}
            </div>
          ) : (
            filtered.map((c, i) => {
              const isActive = i === highlight
              const isCurrent = c.obj_name === value
              return (
                <button
                  key={c.obj_name}
                  type="button"
                  onClick={() => onPick(c.obj_name)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    minHeight: ITEM_HEIGHT,
                    padding: '8px 12px',
                    display: 'flex', flexDirection: 'column', gap: 2,
                    border: 'none',
                    background: isActive ? 'var(--color-accent-weak)' : 'transparent',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderLeft: `3px solid ${isCurrent ? 'var(--color-accent)' : 'transparent'}`,
                  }}
                >
                  <span style={{
                    fontSize: '0.9125rem',
                    fontWeight: isCurrent ? 600 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {c.title && c.title !== c.obj_name ? c.title : c.obj_name}
                  </span>
                  <span style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {c.obj_name}{c.hint ? ` · ${c.hint}` : ''}
                  </span>
                </button>
              )
            })
          )}
        </div>
      )}

      {/* Custom-name mode */}
      {customMode && (
        <div style={{
          padding: 12,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
            Enter a name for a {typeLabel} you'll create later. The button will
            navigate there once that {typeLabel} exists.
          </div>
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customName.trim()) {
                e.preventDefault()
                onPick(customName.trim())
              }
            }}
            placeholder="obj_name"
            style={{ ...SEARCH_INPUT, paddingLeft: 10 }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setCustomMode(false)}
              className="btn btn-ghost btn-sm"
            >Back to list</button>
            <button
              type="button"
              onClick={() => customName.trim() && onPick(customName.trim())}
              className="btn btn-sm"
              disabled={!customName.trim()}
              style={{
                background: 'var(--color-accent)', color: '#fff',
                opacity: customName.trim() ? 1 : 0.5,
              }}
            >Use this name</button>
          </div>
        </div>
      )}

      {/* Footer — counts + "Use custom name" toggle */}
      {!customMode && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', borderTop: '1px solid var(--color-border)',
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          background: 'var(--color-bg)',
        }}>
          <span>
            {filtered.length === catalog.length
              ? `${catalog.length} ${typeLabel}${catalog.length === 1 ? '' : 's'}`
              : `${filtered.length} of ${catalog.length}`}
          </span>
          {allowCustom && (
            <button
              type="button"
              onClick={() => setCustomMode(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'transparent', border: 'none', padding: '2px 6px',
                cursor: 'pointer', color: 'var(--color-accent)',
                borderRadius: 4, fontSize: '0.8125rem', fontWeight: 600,
              }}
              title="Type a name for an object you'll create later"
            >
              <Edit2 size={11} />
              Use custom name
            </button>
          )}
        </div>
      )}
    </div>
  )
}
